import { createContext, useContext } from 'react'
import type { TenantSettings } from '../../api/types'

/* The editable settings draft, handed down to whichever tab is showing.

   It used to arrive through the router's outlet context, which tied the form
   to a route — every tab had to be its own URL. Settings is a dialog now, so
   the draft travels through a real React context instead: the same single
   fetch and single Save serve all five tabs whatever the URL is. */
export interface SettingsFormContext {
  draft: TenantSettings
  set: <K extends keyof TenantSettings>(key: K, value: TenantSettings[K]) => void
  /**
   * Switches to another tab of the same dialog.
   *
   * A tab is a piece of state here, not a route, so a warning on one tab cannot
   * link to the control that fixes it with an <a>. This is what lets the
   * Mapping tab send the user straight to the field its toggle is waiting on
   * rather than telling them to go and find it.
   */
  goToTab: (tab: 'general' | 'import' | 'mapping' | 'validation' | 'items') => void
}

export const SettingsFormContext = createContext<SettingsFormContext | null>(null)

export function useSettingsForm(): SettingsFormContext {
  const context = useContext(SettingsFormContext)
  if (!context) {
    throw new Error('useSettingsForm must be used inside the settings dialog.')
  }
  return context
}

/** The settings whose value is a plain on/off, i.e. the ones SettingsToggle can drive. */
export type BooleanSettingKey = {
  [K in keyof TenantSettings]: TenantSettings[K] extends boolean ? K : never
}[keyof TenantSettings]
